import { useState } from 'react'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { Select } from '../../components/atoms/Select'
import { Spinner } from '../../components/atoms/Spinner'
import { Th, Td } from '../../components/molecules/Table'
import { TableShell } from '../../components/molecules/TableShell'
import { Field } from '../../components/molecules/Field'
import { Paginator } from '../../components/molecules/Paginator'
import {
  useProductSuppliers,
  type ProductSupplierLinkDto
} from '../../../viewmodels/useProductSuppliers'
import { useAuth } from '../../../context/AuthContext'
import { SCREENCODES } from '../../../constants/screen.constants'

/**
 * /manage/product-suppliers — pure M:N catalog of which suppliers can
 * supply which products. Add + soft-delete; no edit (a link is a
 * (product, supplier) pair, so change = delete + add).
 */
export function ProductSuppliersPage(): JSX.Element {
  const vm = useProductSuppliers()
  const { can } = useAuth()
  const canAdd = can(SCREENCODES.MANAGEPRODUCTSUPPLIERS, 'add')
  const canDelete = can(SCREENCODES.MANAGEPRODUCTSUPPLIERS, 'delete')

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-heading text-h2 text-charcoal">Product Suppliers</h1>
        <p className="font-body text-body-sm text-text-secondary mt-1">
          Which suppliers can supply which products. Pure relationship rows — no pricing,
          no quantities. To change a link, delete it and add a new one.
        </p>
      </header>

      {vm.error !== null ? (
        <Alert tone="error">
          {vm.error}{' '}
          <button
            type="button"
            className="underline underline-offset-2 hover:text-charcoal"
            onClick={() => vm.reload()}
          >
            Retry
          </button>
        </Alert>
      ) : null}

      {canAdd ? <AddForm vm={vm} /> : null}

      {vm.isLoading ? (
        <div className="flex items-center justify-center py-12"><Spinner /></div>
      ) : vm.items.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          <Table
            items={vm.items}
            canDelete={canDelete}
            onDelete={vm.remove}
          />
          <Paginator
            page={vm.page}
            pageSize={vm.pageSize}
            total={vm.total}
            onPageChange={vm.setPage}
            disabled={vm.isLoading}
          />
        </>
      )}
    </div>
  )
}

function EmptyState(): JSX.Element {
  return (
    <div className="text-center py-16 border border-border rounded-md bg-parchment/40">
      <p className="font-heading text-h3 text-charcoal">No product-supplier links yet</p>
      <p className="font-body text-body-sm text-text-secondary mt-2">
        Pick a product and a supplier above to add the first link.
      </p>
    </div>
  )
}

function AddForm({
  vm
}: {
  readonly vm: ReturnType<typeof useProductSuppliers>
}): JSX.Element {
  const [productId, setProductId] = useState<string>('')
  const [supplierId, setSupplierId] = useState<string>('')
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [fieldError, setFieldError] = useState<{ field: 'product_id' | 'supplier_id'; message: string } | null>(null)
  const [formError, setFormError] = useState<string | null>(null)

  const handleAdd = async (): Promise<void> => {
    setFieldError(null)
    setFormError(null)
    if (productId === '') {
      setFieldError({ field: 'product_id', message: 'Pick a product' })
      return
    }
    if (supplierId === '') {
      setFieldError({ field: 'supplier_id', message: 'Pick a supplier' })
      return
    }
    setSubmitting(true)
    const r = await vm.add({ product_id: productId, supplier_id: supplierId })
    setSubmitting(false)
    if (!r.ok) {
      if (r.field === 'product_id' || r.field === 'supplier_id') {
        setFieldError({ field: r.field, message: r.message })
      } else {
        setFormError(r.message)
      }
      return
    }
    setProductId('')
    setSupplierId('')
  }

  return (
    <div className="border border-border rounded-md bg-parchment/40 p-4 space-y-3">
      <p className="font-body text-body-sm font-medium text-charcoal">Add a link</p>
      {formError !== null ? <Alert tone="error">{formError}</Alert> : null}
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
        <Field
          label="Product"
          error={fieldError?.field === 'product_id' ? fieldError.message : null}
        >
          <Select
            value={productId}
            hasError={fieldError?.field === 'product_id'}
            onChange={(e) => setProductId(e.target.value)}
            aria-label="Product"
          >
            <option value="">— Select a product —</option>
            {vm.products.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </Select>
        </Field>
        <Field
          label="Supplier"
          error={fieldError?.field === 'supplier_id' ? fieldError.message : null}
        >
          <Select
            value={supplierId}
            hasError={fieldError?.field === 'supplier_id'}
            onChange={(e) => setSupplierId(e.target.value)}
            aria-label="Supplier"
          >
            <option value="">— Select a supplier —</option>
            {vm.suppliers.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </Select>
        </Field>
        <Button onClick={() => void handleAdd()} disabled={submitting}>
          {submitting ? 'Adding…' : 'Add'}
        </Button>
      </div>
    </div>
  )
}

function Table({
  items,
  canDelete,
  onDelete
}: {
  readonly items: ProductSupplierLinkDto[]
  readonly canDelete: boolean
  readonly onDelete: (id: string) => Promise<{ ok: true } | { ok: false; message: string }>
}): JSX.Element {
  const [busyId, setBusyId] = useState<string | null>(null)
  const [rowError, setRowError] = useState<string | null>(null)

  const handleDelete = async (row: ProductSupplierLinkDto): Promise<void> => {
    if (typeof window !== 'undefined'
      && !window.confirm(`Remove the link between "${row.product_name}" and "${row.supplier_name}"?`)) return
    setBusyId(row.id)
    setRowError(null)
    const r = await onDelete(row.id)
    setBusyId(null)
    if (!r.ok) setRowError(r.message)
  }

  return (
    <div className="space-y-3">
      {rowError !== null ? <Alert tone="error">{rowError}</Alert> : null}
      <TableShell>
        <table className="w-full font-body text-body-sm">
          <thead className="bg-parchment/50 text-text-secondary">
            <tr>
              <Th>Product</Th>
              <Th>Supplier</Th>
              <Th className="text-right">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {items.map((row) => (
              <tr key={row.id} className="border-t border-border">
                <Td>{row.product_name}</Td>
                <Td>{row.supplier_name}</Td>
                <Td className="text-right whitespace-nowrap">
                  {canDelete ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => void handleDelete(row)}
                      disabled={busyId === row.id}
                    >
                      {busyId === row.id ? 'Removing…' : 'Remove'}
                    </Button>
                  ) : null}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableShell>
    </div>
  )
}
